import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTicketApi } from "@/api/tickets";

/** 每组最多取几个。刻意取小：组多时弹层不至于太长，收窄靠继续输入而不是翻页。 */
const MAX_PER_GROUP = 5;
const SEARCH_DEBOUNCE_MS = 200;

/**
 * @提及候选。搜索与分组都在服务端完成（`GET tickets/{id}/mention-candidates`）：
 * 按「工单当前所属组 → 本项目的项目组 → 职能组 → 未编组成员」排序，组内跟进人与
 * 已在场的协作者优先，因此前端只管照原顺序渲染，不要自己再排一遍。
 *
 * `query` 传 null 表示 @ 菜单没打开，此时不发请求。
 */
export function useMentionCandidates(ticketId: string | undefined, query: string | null) {
  const ticketApi = useTicketApi();
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    if (query === null) {
      return;
    }
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const enabled = Boolean(ticketId) && query !== null;

  const candidatesQuery = useQuery({
    queryKey: ["tickets", "mention-candidates", ticketId, debouncedQuery],
    queryFn: ({ signal }) => ticketApi.getMentionCandidates(ticketId as string, { filter: debouncedQuery || undefined, maxPerGroup: MAX_PER_GROUP }, signal),
    enabled,
    // 边打字边换关键词时保住上一批结果，弹层不会闪成空白再填回来。
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const groups = useMemo(() => candidatesQuery.data ?? [], [candidatesQuery.data]);

  // 键盘上下键要跨组连续移动，这里拍平一份供导航用；渲染仍按组走。
  const flatCandidates = useMemo(() => groups.flatMap((group) => group.agents), [groups]);

  const indexByAgentId = useMemo(() => new Map(flatCandidates.map((candidate, index) => [candidate.agentId, index])), [flatCandidates]);

  return {
    groups,
    flatCandidates,
    indexByAgentId,
    /** 首次加载（换关键词时因为 keepPreviousData 不算 pending）。 */
    isLoading: enabled && candidatesQuery.isPending,
    isError: candidatesQuery.isError,
    /** 关键词已变、结果还在路上。 */
    isSearching: enabled && candidatesQuery.isFetching && !candidatesQuery.isPending,
  };
}
